import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useEffect, useState } from 'react';
import useFindInventoryForDeletionLazy from '@src/adapters/hooks/inventory/useFindInventoryForDeletion';
import InventoryDeletionDTO from '@src/adapters/dto/inventory/InventoryDeleteDTO';
import InventoryAutoComplete from '@src/components/inventory/InventoryAutoComplete';
import { useForm, useWatch } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import useDeleteInventoryLazy from '@src/adapters/hooks/inventory/useDeleteInventory';

interface Props {
  id: string;
  open: boolean;
  close(isDeleted: boolean): void;
}

interface FormValues {
  inventory: { id: string | null; name: string | null };
}

function InventoryDelete({ id, open, close }: Props) {
  const { execute: find } = useFindInventoryForDeletionLazy();

  const { execute: deleteItem } = useDeleteInventoryLazy();
  const { enqueueSnackbar } = useSnackbar();
  const [item, setItem] = useState<InventoryDeletionDTO | null>(null);
  const { control, handleSubmit, setValue } = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      inventory: {
        id: null,
        name: null,
      },
    },
  });

  const selectedInventory = useWatch({ control, name: 'inventory' });

  useEffect(() => {
    const getItemToDelete = async () => {
      const result = await find({ id });
      setItem(result.inventory);
    };

    if (open) {
      getItemToDelete();
    }
  }, [find, id, open]);

  const onSubmit = handleSubmit(async (data) => {
    if (item && item.cardItemCount > 0 && !selectedInventory.id) {
      enqueueSnackbar(
        'An inventory to change the card items to must be selected',
        { variant: 'error' }
      );
    } else {
      await deleteItem({
        inventoryId: id,
        migrateId: selectedInventory ? selectedInventory.id! : undefined,
      });
      close(true);
    }
  });

  return (
    <Dialog open={open} aria-labelledby="Delete Inventory" maxWidth="lg">
      <form onSubmit={onSubmit}>
        <DialogTitle
          id="delete-dialog-title"
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            alignContent: 'center',
            gap: theme.spacing(2),
          })}>
          <DeleteForeverIcon
            sx={(theme) => ({ color: theme.palette.error.main })}
          />
          Permanently Delete Inventory
        </DialogTitle>
        <DialogContent>
          {item && item.cardItemCount === 0 && (
            <Box sx={{ width: '350px', padding: '20px' }}>
              This inventory is not linked to any card items and can safely be
              removed
            </Box>
          )}
          {item && item.cardItemCount > 0 && (
            <Box sx={{ width: '350px', padding: '20px' }}>
              This inventory is linked to {item?.cardItemCount} card item
              {item?.cardItemCount > 1 ? 's' : ''}, please select which
              inventory {item?.cardItemCount > 1 ? 'these' : 'this'} card item
              {item?.cardItemCount > 1 ? 's' : ''} should be moved to?
            </Box>
          )}
          {item && item.cardItemCount > 0 && (
            <Box sx={{ width: '350px', padding: '20px' }}>
              <InventoryAutoComplete
                control={control}
                name="inventory"
                label="Inventory"
                exclude={[id]}
                setValue={setValue}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => close(false)}
            variant="outlined"
            color="primary">
            Close
          </Button>
          <Button type="submit" variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default InventoryDelete;
